// src/components/features/Elevation/pages/ElevationFormPage.jsx
// URL: /elevation/:projectId?token=xxx&updatedby=admin
//
// EXACT STEP ORDER:
// ── Planning steps (reused) ──────────────────────────────────────────────────
//  0 → Step1PlotShape             "Select your plot shape"
//  1 → Step2PlotDirection         "Select Your Plot Facing Direction"
//  2 → Step3Peripheries           "Define Surroundings of Your Plot"
//  3 → Step4PlotSize              "Select your plot Dimensions"
//  4 → Step6ProjectType           "Confirm Your Project Type & Floors"
//  5 → Step8PlotLevelInformation  "Where is your plot level compared to the road?"
//  6 → Step9StairType             "Stair Type"
//  7 → Step16WallDimentions       "Building Heights"
//  8 → Step19AttachRegistryCopy   "Attach Files"
// ── Elevation-specific steps ─────────────────────────────────────────────────
//  9 → ElevationStepType              "Elevation Type" (Economy / Modern / Luxury + budget bar)
// 10 → ElevationStep1Theme            "Elevation Theme" (swipe + thumbnail grid)
// 11 → ElevationStep5Lights           "Elevation Lighting" (swipe)
// 12 → ElevationStep6CncTerracotta    "CNC / Terracotta Work" (swipe)
// 13 → ElevationStep7Material         "Material Selection" (swipe)
// 14 → ElevationStep4StaircaseGlass   "Staircase Glass / Window Pattern" (radio)
// 15 → ElevationStep8Windows          "Windows" (radio)
// 16 → ElevationStep2Gate             "Gate Selection" (radio)
// 17 → ElevationStep9Ceiling          "Ceiling Design" (swipe)
// 18 → ElevationStep10Parapet         "Parapet Design" (swipe)
// 19 → ElevationStep3Pergola          "Pergola" (swipe)
// 20 → ElevationStep11StaircasePattern "Staircase Pattern (Open/Closed)" (radio)

import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  Box, Flex, useColorModeValue, useBreakpointValue,
  useToast, Spinner, Center, Text, Badge,
} from "@chakra-ui/react";

import {
  isAnyAuthenticated, saveRedirectUrl, saveAdminToken,
  getAdminToken, getUpdatedBy, getCustomerToken,
} from "../../../../utils/storage";
import {
  fetchFormData, postElevationFormData, putElevationFormData, moveCardToInProgress,
} from "../../../../utils/api";

// ── Planning steps (exact same imports as PlanningFormPage) ──────────────────
import Step1PlotShape from "../../Planning/components/Step1PlotShape";
import Step2PlotDirection from "../../Planning/components/Step2Direction";
import Step3Peripheries from "../../Planning/components/Step3Peripheries";
import Step4PlotSize from "../../Planning/components/Step4PlotSize";
import Step4PlotSizeNonRectangular from "../../Planning/components/Step4PlotSizeNonRectengular";
import Step6ProjectType from "../../Planning/components/Step6ProjecttypeandNumberofFloors";
import Step8PlotLevelInformation from "../../Planning/components/Step8PlotLevelInformation";
import Step9StairType from "../../Planning/components/Step9StairType";
import Step16WallDimentions from "../../Planning/components/Step16WallDimentions";
import Step19AttachRegistryCopy from "../../Planning/components/Step19AttachRegistryCopy";

// ── Elevation-specific steps ──────────────────────────────────────────────────
import ElevationStepType from "../components/ElevationStepType";
import ElevationStep1Theme from "../components/ElevationStep1Theme";
import ElevationStep2Gate from "../components/ElevationStep2Gate";
import ElevationStep3Pergola from "../components/ElevationStep3Pergola";
import ElevationStep4StaircaseGlass from "../components/ElevationStep4StaircaseGlass";
import ElevationStep5Lights from "../components/ElevationStep5Lights";
import ElevationStep6CncTerracotta from "../components/ElevationStep6CncTerracotta";
import ElevationStep7Material from "../components/ElevationStep7Material";
import ElevationStep8Windows from "../components/ElevationStep8Windows";
import ElevationStep9Ceiling from "../components/ElevationStep9Ceiling";
import ElevationStep10Parapet from "../components/ElevationStep10Parapet";
import ElevationStep11StaircasePattern from "../components/ElevationStep11StaircasePattern";

// ─── Default form data ────────────────────────────────────────────────────────
const DEFAULT_FORM_DATA = {
  // Planning shared fields (same keys as PlanningFormPage)
  shape: "rectangular",
  direction: "north",
  peripheries: { right: "", back: "", left: "" },
  roadWidthFeet: "",
  roadWidthInches: "",
  size: { widthFeet: "", widthInches: "0", depthFeet: "", depthInches: "0" },
  projectType: "",
  lifestyleType: "",
  numberOfFloors: "",
  floorsData: [],
  buildingArea: null,
  levelRelation: "same",
  stair: { type: "", riser: "", tread: "" },
  tread: "",
  riser: "",
  plinthLevelFeet: "", plinthLevelInches: "0",
  lintelLevelFeet: "", lintelLevelInches: "0",
  sillLevelFeet: "", sillLevelInches: "0",
  slabHeightFeet: "", slabHeightInches: "0",
  slab: { sides: "", slabExtension: "" },
  attachPlotImage: "no",
  // Elevation-specific
  elevationType: "",
  elevationTheme: "",
  elevationLikedThemes: [],
  elevationLights: "",
  elevationLikedLights: [],
  elevationCncTerracotta: "",
  elevationLikedCncTerracotta: [],
  elevationMaterial: "",
  elevationLikedMaterial: [],
  elevationStaircaseGlass: "",
  elevationWindows: "",
  elevationGate: "",
  elevationCeiling: "",
  elevationLikedCeiling: [],
  elevationParapet: "",
  elevationLikedParapet: [],
  elevationPergola: "",
  elevationLikedPergola: [],
  elevationStaircasePattern: "",
};

// 9 planning + 12 elevation = 21 total
const TOTAL_STEPS = 21;
const PLANNING_STEPS = 9;   // steps 0-8
const ELEVATION_START = 9;   // steps 9-20
const ELEVATION_STEPS = 12;  // Type, Theme, Lights, CNC/Terracotta, Material,
                              // StaircaseGlass, Windows, Gate, Ceiling, Parapet,
                              // Pergola, StaircasePattern

export default function ElevationFormPage() {
  const { projectId: projectIdFromPath } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const toast = useToast();

  // "/elevation/:projectId" (path param) aur "/elevation?projectId=xxx"
  // (query param) — dono URL shapes se projectId mil sake, isliye fallback.
  const projectId =
    projectIdFromPath ||
    searchParams.get("projectId") ||
    searchParams.get("pid") ||
    searchParams.get("id") ||
    "";

  const urlToken = searchParams.get("token") || "";
  const urlUpdatedBy = searchParams.get("updatedby") || "";
  const formVersion = searchParams.get("version") || "";

  const viewMode = searchParams.get("viewMode") === "true";

  if (urlToken) saveAdminToken(urlToken, urlUpdatedBy);

  const isAdmin = !!(urlToken || (urlUpdatedBy === "admin" && getAdminToken()));
  const token = urlToken || (isAdmin ? getAdminToken() : getCustomerToken()) || "";
  const updatedBy = isAdmin ? "admin" : (urlUpdatedBy || getUpdatedBy() || "user");

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [apiError, setApiError] = useState(null);
  const [step, setStep] = useState(0);
  const [formData, setFormData] = useState(DEFAULT_FORM_DATA);
  const [version, setVersion] = useState(null);
  const moveCardCalled = useRef(false);

  const containerWidth = useBreakpointValue({ base: "100%", md: "400px" });
  const pageBg = useColorModeValue("gray.100", "gray.900");
  const cardBg = useColorModeValue("gray.50", "gray.800");

  // ── Fetch existing data ─────────────────────────────────────────────────────
  const initialize = useCallback(async () => {
    if (!token && !isAnyAuthenticated()) {
      saveRedirectUrl(window.location.pathname + window.location.search);
      toast({ title: "Authentication Required", status: "info", duration: 3000, isClosable: true });
      setTimeout(() => navigate("/login"), 1500);
      return;
    }
    if (!projectId) { setIsLoading(false); return; }

    try {
      setIsLoading(true);
      setApiError(null);
      const apiData = await fetchFormData(projectId, token, updatedBy, formVersion);

      if (apiData._meta?.status === "submitted" && !viewMode) { navigate("/thank-you"); return; }
      if (apiData._meta?.version) setVersion(apiData._meta.version);

      const { _meta, ...clean } = apiData;
      setFormData((prev) => ({ ...DEFAULT_FORM_DATA, ...prev, ...clean }));

      toast({
        title: "Project loaded ✓",
        description: `${projectId} — ${updatedBy === "admin" ? "Admin" : "User"} mode`,
        status: "success", duration: 3000, isClosable: true, position: "top",
      });

      if (!moveCardCalled.current && !viewMode) {
        moveCardCalled.current = true;
        await moveCardToInProgress(projectId, token, updatedBy, apiData._meta?.version || formVersion);
      }
    } catch (err) {
      console.error("Elevation fetch failed:", err);
      setApiError(err.message);
      if (!viewMode) {
        toast({
          title: "Could not load project",
          description: `${err.message} — using blank form`,
          status: "warning", duration: 5000, isClosable: true, position: "top",
        });
      }
    } finally {
      setIsLoading(false);
    }
  }, [projectId, token, updatedBy, navigate, toast, viewMode, formVersion]);

  useEffect(() => { initialize(); }, [initialize]);

  // ── PUT on every Next ───────────────────────────────────────────────────────
  const putToApi = async (data) => {
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try {
      await putElevationFormData(projectId, data, updatedBy, token, version);
    } catch (err) {
      toast({ title: "Step save failed", description: err.message, status: "error", duration: 3000, isClosable: true, position: "top-right" });
    } finally { setIsSaving(false); }
  };

  // ── POST on final submit ────────────────────────────────────────────────────
  const postToApi = async (data) => {
    if (!projectId || viewMode) return;
    setIsSaving(true);
    try {
      await postElevationFormData(projectId, data, updatedBy, token, version);
    } catch (err) {
      toast({ title: "Save failed", description: err.message, status: "error", duration: 3000, isClosable: true, position: "top-right" });
    } finally { setIsSaving(false); }
  };

  const nextStep = async (latestData) => {
    await putToApi(latestData ?? formData);
    setStep((p) => Math.min(p + 1, TOTAL_STEPS - 1));
  };

  const prevStep = () => setStep((p) => Math.max(p - 1, 0));
  const isLastStep = step === TOTAL_STEPS - 1;

  const handleSubmit = async (latestData) => {
    if (viewMode) return;
    await postToApi(latestData ?? formData);
    navigate("/thank-you");
  };

  // ── Render steps ─────────────────────────────────────────────────────────────
  const renderStep = () => {
    const props = {
      formData, setFormData,
      onBack: prevStep, onNext: nextStep, onSubmit: handleSubmit,
      isLastStep, projectId, updatedBy, token,
    };
    switch (step) {
      // ── Planning steps ──────────────────────────────────────────────────────
      case 0: return <Step1PlotShape {...props} />;
      case 1: return <Step2PlotDirection {...props} />;
      case 2: return <Step3Peripheries {...props} />;
      case 3: return formData.shape === "rectangular"
        ? <Step4PlotSize {...props} />
        : <Step4PlotSizeNonRectangular {...props} />;
      case 4: return <Step6ProjectType {...props} />;
      case 5: return <Step8PlotLevelInformation {...props} />;
      case 6: return <Step9StairType {...props} />;
      case 7: return <Step16WallDimentions {...props} />;
      case 8: return <Step19AttachRegistryCopy {...props} />;
      // ── Elevation steps ─────────────────────────────────────────────────────
      case 9: return <ElevationStepType               {...props} />;
      case 10: return <ElevationStep1Theme             {...props} />;
      case 11: return <ElevationStep5Lights            {...props} />;
      case 12: return <ElevationStep6CncTerracotta     {...props} />;
      case 13: return <ElevationStep7Material          {...props} />;
      case 14: return <ElevationStep4StaircaseGlass    {...props} />;
      case 15: return <ElevationStep8Windows           {...props} />;
      case 16: return <ElevationStep2Gate              {...props} />;
      case 17: return <ElevationStep9Ceiling           {...props} />;
      case 18: return <ElevationStep10Parapet          {...props} />;
      case 19: return <ElevationStep3Pergola           {...props} />;
      case 20: return <ElevationStep11StaircasePattern {...props} />;
      default: return null;
    }
  };

  const isElevationStep = step >= ELEVATION_START;

  if (isLoading) {
    return (
      <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
        <Center flexDirection="column" gap={4}>
          <Spinner size="xl" color="cyan.500" thickness="4px" />
          <Text color="gray.500" fontSize="sm">Loading {projectId}...</Text>
        </Center>
      </Flex>
    );
  }

  return (
    <Flex minH="100vh" align="center" justify="center" bg={pageBg}>
      <Box
        w={containerWidth}
        bg={cardBg}
        px={6} py={6}
        borderRadius="lg"
        boxShadow="lg"
        display="flex"
        flexDirection="column"
      >
        {/* Header */}
        <Flex justify="space-between" align="center" mb={2}>
          <Flex align="center" gap={2} flexWrap="wrap">
            <Text fontSize="11px" color="gray.400" fontWeight="500">
              Step {step + 1} / {TOTAL_STEPS}
            </Text>
            {projectId && (
              <Badge fontSize="10px" colorScheme="cyan" variant="subtle">{projectId}</Badge>
            )}
            {updatedBy === "admin" && (
              <Badge fontSize="10px" colorScheme="purple" variant="subtle">Admin</Badge>
            )}
            <Badge fontSize="10px" colorScheme={isElevationStep ? "orange" : "orange"} variant="subtle">
              {isElevationStep ? "🏠 Elevation" : "🏠 Elevation"}
            </Badge>
            {viewMode && (
              <Badge fontSize="10px" colorScheme="orange" variant="solid">
                View Mode
              </Badge>
            )}
          </Flex>
          <Flex align="center" gap={1}>
            {isSaving && <Text fontSize="10px" color="cyan.500" fontWeight="600">Saving...</Text>}
            {apiError && <Badge fontSize="10px" colorScheme="orange" variant="subtle">⚠ Offline</Badge>}
          </Flex>
        </Flex>

        {/* Two-section progress bar */}
        <Box mb={4}>
          <Flex gap="2px" borderRadius="full" overflow="hidden">
            {/* Planning section dots (9 steps) */}
            {Array.from({ length: PLANNING_STEPS }).map((_, i) => (
              <Box
                key={i}
                flex="1"
                h="3px"
                bg={i < step ? "gray.500" : i === step ? "gray.600" : "gray.200"}
                transition="background 0.2s"
              />
            ))}
            {/* Small gap between sections */}
            <Box w="4px" bg={cardBg} flexShrink={0} />
            {/* Elevation section dots (12 steps) */}
            {Array.from({ length: ELEVATION_STEPS }).map((_, i) => {
              const globalStep = ELEVATION_START + i;
              return (
                <Box
                  key={i}
                  flex="1"
                  h="3px"
                  bg={
                    step > globalStep ? "cyan.500"
                      : step === globalStep ? "cyan.400"
                        : "gray.200"
                  }
                  transition="background 0.2s"
                />
              );
            })}
          </Flex>
          <Flex justify="space-between" mt="3px">
            <Text fontSize="9px" color="gray.400">Site Info</Text>
            <Text fontSize="9px" color={isElevationStep ? "cyan.500" : "gray.300"} fontWeight={isElevationStep ? "600" : "400"}>
              Elevation Design
            </Text>
          </Flex>
        </Box>

        <Box flex="1">{renderStep()}</Box>
      </Box>
    </Flex>
  );
}